import { createRoute, redirect } from "@tanstack/react-router";
import { BetterAuthCallbackPage } from "pages/auth/BetterAuthCallbackPage";
import { BetterAuthConsentPage } from "pages/auth/BetterAuthConsentPage";
import { BetterAuthEmailChangePage } from "pages/auth/BetterAuthEmailChangePage";
import { BetterAuthEmailConfirmedPage } from "pages/auth/BetterAuthEmailConfirmedPage";
import { BetterAuthForgotPasswordPage } from "pages/auth/BetterAuthForgotPasswordPage";
import { BetterAuthLoginPage } from "pages/auth/BetterAuthLoginPage";
import { BetterAuthRegisterPage } from "pages/auth/BetterAuthRegisterPage";
import { BetterAuthResetPasswordPage } from "pages/auth/BetterAuthResetPasswordPage";
import { BetterAuthTwoFactorPage } from "pages/auth/BetterAuthTwoFactorPage";
import { BetterAuthTwoFactorSetupPage } from "pages/auth/BetterAuthTwoFactorSetupPage";
import { BetterAuthVerifyEmailPage } from "pages/auth/BetterAuthVerifyEmailPage";

import { betterAuthSessionQueryOptions } from "lib/hooks/api/auth/betterAuthSessionQuery";
import { rootRoute, TWO_FACTOR_SETUP_PATH } from "../rootRoute";
import {
  canSkipLoginFor,
  validateCallbackSearch,
  validateConsentSearch,
  validateEmailChangeSearch,
  validateEmailConfirmedSearch,
  validateForgotPasswordSearch,
  validateLoginSearch,
  validateResetPasswordSearch,
  validateSignupSearch,
  validateTwoFactorSearch,
  validateVerifyEmailSearch,
} from "./betterAuthSearch";

// Login/register pages under /auth/*. `noShell` renders them bare — no app shell, no header bar —
// so each page centres its card in the viewport and carries the Stotles lockup itself (see
// authPageStyles / StotlesLogo). Rails serves the SPA shell unauthenticated via spa#public (see
// config/routes.rb). `validateSearch` (see betterAuthSearch.ts) captures the sanitised post-login
// `redirect` destination.

export const betterAuthLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/login",
  staticData: { pageName: "Log In", noShell: true },
  validateSearch: validateLoginSearch,
  // Skip the form for a user already signed in to both Rails and better-auth. Either one alone isn't
  // enough: pages send a Rails-only user here to get a better-auth session, and a user who still owes
  // 2FA setup has only a better-auth one, so Rails would bounce them straight back.
  beforeLoad: async ({ context, search }) => {
    if (!window.userSignedIn || !canSkipLoginFor(search.redirect)) return;
    // Always refetch: the cached session never goes stale, and pages send users here via SPA
    // navigation when better-auth's live session has ended since the cache was filled.
    const session = await context.queryClient.fetchQuery({
      ...betterAuthSessionQueryOptions(),
      staleTime: 0,
    });
    if (session) throw redirect({ href: search.redirect ?? "/", reloadDocument: true });
  },
  component: BetterAuthLoginPage,
});

export const betterAuthRegisterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/register",
  staticData: { pageName: "Register", noShell: true },
  // Captures the signup context + attribution marketing puts on the URL (see betterAuthSearch.ts).
  validateSearch: validateSignupSearch,
  component: BetterAuthRegisterPage,
});

// Landing page for the Google / Microsoft SSO redirect. Resolves the new session and forwards the
// user on (or shows an error). See signInWithProvider / BetterAuthCallbackPage.
export const betterAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  staticData: { pageName: "Signing in", noShell: true },
  validateSearch: validateCallbackSearch,
  component: BetterAuthCallbackPage,
});

// 2FA challenge page. BetterAuthLoginPage sends a 2FA-enrolled user here after a correct password;
// for social sign-in, the auth-service redirects them here after the OAuth callback.
export const betterAuthTwoFactorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/2fa",
  staticData: { pageName: "Two-factor authentication", noShell: true },
  validateSearch: validateTwoFactorSearch,
  component: BetterAuthTwoFactorPage,
});

// 2FA enrolment page for a user whose company mandates 2FA but who hasn't set it up. The root-route
// guard redirects here from every other page (see rootRoute.ensure2FASetup); `redirect` is where
// they were heading.
export const betterAuthTwoFactorSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: TWO_FACTOR_SETUP_PATH,
  staticData: { pageName: "Set up two-factor authentication", noShell: true },
  validateSearch: validateTwoFactorSearch,
  component: BetterAuthTwoFactorSetupPage,
});

// "Confirm your email" step for a social sign-in whose provider didn't vouch for the address. The
// auth-service withholds the session, emails a link and redirects here (see
// social-email-verification.ts); `email` is carried on the URL because there's no session to read.
export const betterAuthVerifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/verify-email",
  staticData: { pageName: "Confirm your email", noShell: true },
  validateSearch: validateVerifyEmailSearch,
  component: BetterAuthVerifyEmailPage,
});

// Landing page for an emailed verification link. better-auth's /verify-email takes one `callbackURL`
// for both outcomes, so this page forwards into the app on success and explains the failure
// otherwise. See verificationCallbackUrl for how the link is built.
export const betterAuthEmailConfirmedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/email-confirmed",
  staticData: { pageName: "Email confirmed", noShell: true },
  validateSearch: validateEmailConfirmedSearch,
  component: BetterAuthEmailConfirmedPage,
});

// Landing page for both links of an email change. Public because neither link needs a session, so
// the result has to show on whichever device or browser the email was opened in.
export const betterAuthEmailChangeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/email-change",
  staticData: { pageName: "Email change", noShell: true },
  validateSearch: validateEmailChangeSearch,
  component: BetterAuthEmailChangePage,
});

// Unauthenticated "forgot password" entry point, linked from the login page. Collects an email and
// kicks off the reset flow; the emailed link returns the user to /auth/reset-password.
// `validateSearch` captures the `email` the login page forwards to pre-fill the field.
export const betterAuthForgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/forgot-password",
  staticData: { pageName: "Forgot password", noShell: true },
  validateSearch: validateForgotPasswordSearch,
  component: BetterAuthForgotPasswordPage,
});

// Landing page for the emailed password-reset link. better-auth validates the token server-side and
// redirects here with `?token=…`; the page lets the user set a new password. See ChangePassword.
export const betterAuthResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/reset-password",
  staticData: { pageName: "Reset password", noShell: true },
  validateSearch: validateResetPasswordSearch,
  component: BetterAuthResetPasswordPage,
});

// OIDC consent screen. Only reachable for an OAuth client registered without `skipConsent` — every
// Stotles-owned client skips it — but the auth-service's `consentPage` option is mandatory, so this
// route has to exist or that flow 404s mid-request. Don't prune it as unused: MCP clients don't skip
// consent, so it's on the critical path of every MCP connect. See BetterAuthConsentPage.
export const betterAuthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/consent",
  staticData: { pageName: "Authorise access", noShell: true },
  validateSearch: validateConsentSearch,
  component: BetterAuthConsentPage,
});

// Flat list of sibling routes, spread into the route tree (mirrors authRoutes).
export const betterAuthRoutes = [
  betterAuthLoginRoute,
  betterAuthRegisterRoute,
  betterAuthCallbackRoute,
  betterAuthTwoFactorRoute,
  betterAuthTwoFactorSetupRoute,
  betterAuthVerifyEmailRoute,
  betterAuthEmailConfirmedRoute,
  betterAuthEmailChangeRoute,
  betterAuthForgotPasswordRoute,
  betterAuthResetPasswordRoute,
  betterAuthConsentRoute,
];
